// `change-dml-target-table` planner — swaps the table an UPDATE / DELETE /
// MERGE / INSERT writes to. Replaces ONLY the qualified name (`[db.][schema.]name`), not
// the whole target item: a trailing alias stays byte-identical, so every SET /
// WHERE / ON reference written through it keeps resolving.

import type { FromItem, PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { replaceEdit } from './applyStructuralEdit.shared';

type ChangeDmlTargetEdit = Extract<StructuralEdit, { op: 'change-dml-target-table' }>;

function targetItem(ctx: EditCtx, target: ChangeDmlTargetEdit['target']): FromItem | undefined {
  if (target === 'update') return ctx.structure.updateTarget;
  if (target === 'delete') return ctx.structure.deleteTarget;
  if (target === 'insert') return ctx.structure.insertTarget;
  return ctx.structure.mergeTarget;
}

export function planChangeDmlTargetTableEdit(
  ctx: EditCtx,
  edit: ChangeDmlTargetEdit,
): readonly PlannedByteEdit[] {
  const item = targetItem(ctx, edit.target);
  if (!item) {
    throw new Error(`change-dml-target-table: the statement has no ${edit.target.toUpperCase()} target`);
  }
  if (item.target.kind !== 'relation') {
    throw new Error(
      `change-dml-target-table: the ${edit.target.toUpperCase()} target is not a plain table (kind: ${item.target.kind})`,
    );
  }
  // `item.range` starts at the first name part; `nameRange` is the terminal
  // name token, so `[start, nameRange.end)` is the whole qualified name.
  return [
    replaceEdit(
      ctx,
      item.range.startOffset,
      item.target.nameRange.endOffset,
      edit.text,
      'change-dml-target-table',
    ),
  ];
}
